import React, { useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";
import { track } from "@/lib/analytics";

const GREETING =
  "Hi! I'm the Atlas assistant. I can help you figure out which country suits your product, explain our services, or send you a free guide. What are you looking to source?";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", text: GREETING }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showCapture, setShowCapture] = useState(false);
  const [captured, setCaptured] = useState(false);
  const [lead, setLead] = useState({ name: "", whatsapp: "", category: "", country: "" });
  const bodyRef = useRef(null);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, showCapture]);

  const toggle = () => {
    setOpen((o) => {
      if (!o) track("chat_started");
      return !o;
    });
  };

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setSending(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      const data = await res.json();
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
      if (data.hot || messages.length >= 4) setShowCapture(true);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "assistant", text: "Sorry, I had trouble. Please tap WhatsApp and we'll help." },
      ]);
    } finally {
      setSending(false);
    }
  };

  const submitCapture = async () => {
    if (!lead.name || !lead.whatsapp) return;
    try {
      await fetch(`${API}/chat/${sessionId || "none"}/capture`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...lead, source: "chat" }),
      });
      track("lead_submit", { source: "chat" });
      setCaptured(true);
      setShowCapture(false);
      setMessages((m) => [
        ...m,
        { role: "assistant", text: "Thanks! Our team will reach out on WhatsApp within a business day." },
      ]);
    } catch (e) {}
  };

  return (
    <>
      <div className={"chatbox" + (open ? " open" : "")} id="cbx" data-testid="chat-panel">
        <div className="ch">
          <span>💬 Atlas Assistant</span>
          <span style={{ cursor: "pointer" }} onClick={toggle} data-testid="chat-close">✕</span>
        </div>
        <div className="cb" id="cbody" ref={bodyRef}>
          {messages.map((m, i) => (
            <div key={i} className={"msg" + (m.role === "user" ? " me" : "")}>
              {m.text}
            </div>
          ))}
          {sending && <div className="msg">…</div>}
          {showCapture && !captured && (
            <div className="msg" style={{ background: "#fff", border: "1px solid var(--line)" }}>
              <b style={{ color: "var(--navy)" }}>Share your details</b>
              <input
                data-testid="chat-lead-name"
                placeholder="Your name"
                value={lead.name}
                onChange={(e) => setLead({ ...lead, name: e.target.value })}
                style={ciStyle}
              />
              <input
                data-testid="chat-lead-whatsapp"
                placeholder="WhatsApp number"
                value={lead.whatsapp}
                onChange={(e) => setLead({ ...lead, whatsapp: e.target.value })}
                style={ciStyle}
              />
              <input
                placeholder="What do you source?"
                value={lead.category}
                onChange={(e) => setLead({ ...lead, category: e.target.value })}
                style={ciStyle}
              />
              <button
                className="btn btn-red"
                style={{ width: "100%", marginTop: 6 }}
                onClick={submitCapture}
                data-testid="chat-lead-submit"
              >
                Send to team
              </button>
            </div>
          )}
        </div>
        <div className="ci">
          <input
            id="cin"
            placeholder="Type your question..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            data-testid="chat-input"
          />
          <button onClick={send} data-testid="chat-send">Send</button>
        </div>
      </div>
      <button className="chatbtn" onClick={toggle} data-testid="chat-open-btn">
        💬 Ask Atlas AI
      </button>
    </>
  );
}

const ciStyle = {
  width: "100%",
  border: "1.5px solid var(--line)",
  borderRadius: 8,
  padding: "9px 10px",
  marginTop: 6,
  fontFamily: "inherit",
  fontSize: 13,
};
